import { useEffect, useState } from 'react';
import { FormProvider, useForm, Controller } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { yupResolver } from '@hookform/resolvers/yup';
import { makeStyles } from 'tss-react/mui';
import clsx from 'clsx';

import SaveIcon from '@mui/icons-material/Save';
import { Box, Theme, Typography, Tooltip } from '@mui/material';

import { handleGetDefendants, mapDefendantsQueryResults } from '../helpers';
import { SettlementCreateFormSubmitProps } from '../types';
import { useSettlementConfig } from '../../../hooks/useSettlementConfig';

import { getCase } from '../../../api/casesApi/casesApi';
import { createSettlement, uploadPdfPreviewConfirm, uploadPdfSettlement } from '../../../api/paymentsApi/settlementsApi';
import FindCaseButton from '../../../components/buttons/FindCaseButton';
import FormSubmitButton from '../../../components/buttons/FormSubmitButton';
import FormCancelButton from '../../../components/buttons/FormCancelButton';
import Loader from '../../../components/Loader';
import FormTextField from '../../../components/formFields/FormTextField';
import FormToggleField from '../../../components/formFields/FormToggleField';
import FormAutocompleteFieldServerSearch from '../../../components/formFields/FormAutocompleteFieldServerSearch';
import FormFileUploadField from '../../../components/formFields/FormFileUploadField';
import { SETTLEMENTS_CREATE_FORM_DEFAULT_VALUES } from '../../../helpers/formSchemas/formDefaultValues';
import { settlementsCreateFormSchema } from '../../../helpers/formSchemas/formSchemas';
import useSnackBar from '../../../hooks/useSnackBar';
import MemoSearchCasePopup from '../../../components/MemoSearchCasePopup'
import CaseInfoPreview from '../../../components/CaseInfoPreview';
import UnableMemoPopup from '../../../components/UnableMemoPopup';
import { memoCaseDetails, SettlementMemoResultSet } from '../../../types';
import WarningIcon from '@mui/icons-material/Warning';
import InfoIcon from '@mui/icons-material/Info';
import theme from '../../../theme';
import { SETTLEMENT_TYPE_OPTIONS } from './constants';


const useStyles = makeStyles()((theme: Theme) => ({
  formWrapper: {
    display: 'flex',
  },
  formContainer: {
    padding: '32px',
    flexBasis: '896px',
    backgroundColor: theme.palette.primary.light,
    borderRadius: '16px',

    '@media (max-width: 850px)': {
      padding: '12px',
    },
  },
  formInfoBlock: {
    display: 'flex',
    flexDirection: 'column',
  },
  formSecondInfoBlock: {
    display: 'flex',
    flexDirection: 'column',
    marginTop: '32px',
  },
  caseInfoBlock: {
    marginTop: '16px',
    display: 'flex',
    width: '50%',
    flexDirection: 'column',
    gap: '16px',
  },
  fieldHeadline: {
    fontWeight: 600,
    fontSize: '20px',
    lineHeight: '24px',
    color: theme.palette.common.white,
    fontFamily: 'Inter',
  },
  loaderOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
  },
  fileName: {
    marginTop: '8px',
    fontSize: '14px',
    color: theme.palette.common.white,
    fontStyle: 'italic',
    fontFamily: 'Inter',
  },
  errorMessageContainer: {
    display: 'flex',
    flexDirection: 'row',
    padding: '10px',
    gap: '12px',
    background: theme.palette.primary.light,
    width: '100%',
    boxSizing: 'border-box',
    borderRadius: '16px',
    border: `1px solid ${theme.palette.error.main}`,
    wordBreak: 'break-word',
  },
  errorMessageText: {
    fontFamily: 'Inter',
    fontSize: '14px',
    fontWeight: 500,
    lineHeight: '100%',
    color: theme.palette.error.main,
    margin: '3px',
  },
  warningMessageContainer: {
    display: 'flex',
    flexDirection: 'row',
    padding: '10px',
    gap: '12px',
    background: theme.palette.primary.main,
    width: '100%',
    boxSizing: 'border-box',
    borderRadius: '16px',
    border: `1px solid ${theme.palette.warning.main}`,
    wordBreak: 'break-word',
  },
  warningMessageText: {
    fontFamily: 'Inter',
    fontSize: '14px',
    fontWeight: 500,
    lineHeight: '100%',
    color: theme.palette.warning.main,
    margin: '3px',
  },
  settlementTypeOptionsContainer: {
    display: 'flex',
    gap: '12px',
    flexWrap: 'wrap',

    // '@media (max-width: 850px)': {
    //   flexDirection: 'column',
    // },
  },
  settlementTypeCard: {
    flex: '0 1 calc(50% - 6px)',
    boxSizing: 'border-box',
    cursor: 'pointer',
    padding: '12px',
    borderRadius: '8px',
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    transition: 'all 0.2s ease',

    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.08)',
    },
  },
  settlementTypeCardSelected: {
    border: `1px solid ${theme.palette.info.main}`,
    backgroundColor: 'rgba(45, 212, 191, 0.1)',

    '&:hover': {
      backgroundColor: 'rgba(45, 212, 191, 0.1)',
    },
  },
  settlementTypeLabel: {
    fontFamily: 'Inter',
    fontSize: '14px',
    fontWeight: 600,
    color: theme.palette.common.white,
    marginBottom: '4px',
  },
  settlementTypeDescription: {
    fontFamily: 'Inter',
    fontSize: '12px',
    color: theme.palette.text.disabled,
    lineHeight: '16px',
  },
}));


const CreateSettlementFormContainer = () => {
  const styles = useStyles();
  const navigate = useNavigate();
  const { setAlert } = useSnackBar();
  const { enableMemoUpload } = useSettlementConfig();

  const caseId = new URLSearchParams(window.location.search).get('caseId');

  const [isFormSubmitting, setIsFormSubmitting] = useState(false);
  const [isMemoUploading, setIsMemoUploading] = useState(false);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [isErrorPopupOpen, setIsErrorPopupOpen] = useState(false);
  const [memoCaseDetails, setMemoCaseDetails] = useState<memoCaseDetails>();
  const [memoDefendantDetails, setMemoDefendantDetails] = useState<{
    id: number;
    name: string;
  }>();
  const [memoData, setMemoData] = useState<SettlementMemoResultSet>();
  const [defendantAutoSelectKey, setDefendantAutoSelectKey] = useState(0);

  const [errorMessages, setErrorMessages] = useState<string[]>([]);
  const [warningMessages, setWarningMessages] = useState<string[]>([]);

  const formMethods = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    resolver: yupResolver(settlementsCreateFormSchema),
    defaultValues: SETTLEMENTS_CREATE_FORM_DEFAULT_VALUES,
  });

  const {
    handleSubmit,
    reset,
    setValue,
    watch,
    trigger,
    formState: { isValid, isDirty },
  } = formMethods;

  const autoFillMemo = watch('autoFillMemo');
  const settlementType = watch('settlementType');
  const showSettledAmount = settlementType !== 'pip';
  const showPipAmount = settlementType === 'pip' || settlementType === 'standard_pip';
  const showLienReimbursementAmount = settlementType === 'lien_repayment';

  // Clear amount fields hidden by the current settlement type
  useEffect(() => {
    if (!showSettledAmount) setValue('amount', 0, { shouldValidate: true });
    if (!showPipAmount) setValue('pipAmount', 0, { shouldValidate: true });
    if (!showLienReimbursementAmount) setValue('lienReimbursementAmount', 0, { shouldValidate: true });
  }, [settlementType]);

  // PIP / lien reimbursement validation depends on the settled amount, so re-run it when amount changes
  const amount = watch('amount');
  useEffect(() => {
    if (settlementType === 'standard_pip' && watch('pipAmount') !== undefined) trigger('pipAmount');
    if (showLienReimbursementAmount && watch('lienReimbursementAmount') !== undefined) {
      trigger('lienReimbursementAmount');
    }
  }, [amount]);

  const handleMemoFileChange = (file: File | null, fieldOnChange: (file: File | null) => void, caseId?: number) => {
    fieldOnChange(file);
    if (autoFillMemo) {
      if (file) {
        setIsMemoUploading(true);
        setErrorMessages([]);
        setWarningMessages([]);
        const finalCaseId = caseId ?? Number(memoCaseDetails?.id);
        uploadPdfSettlement(file, finalCaseId, undefined, settlementType)
          .then((res) => {
            setIsMemoUploading(false);
            setMemoData(res.data);
            setErrorMessages(res.data.errorMessages || []);
            res.data.caseInfo && setMemoCaseDetails({
              id: res.data.caseInfo?.id,
              name: res.data.caseInfo?.name,
              referenceId: res.data.caseInfo?.referenceId,
              typeName: res.data.caseInfo?.type?.name,
            });
            if (res.data.caseInfo?.defendant && Array.isArray(res.data.caseInfo.defendant) && res.data.caseInfo.defendant.length === 1) {
              setMemoDefendantDetails({
                id: res.data.caseInfo.defendant[0].id,
                name: res.data.caseInfo.defendant[0].name || '',
              });
            }
            setWarningMessages(res.data.warningMessages || []);
          })
          .catch((err) => {
            setIsMemoUploading(false);
            const errorMessage = err.response?.data?.message;
            if (errorMessage) {
              // setIsErrorPopupOpen(true);
              setErrorMessages([errorMessage]);
            } else {
              setAlert((prev) => ({
                ...prev,
                message: errorMessage || 'Failed to process memo',
                type: 'error',
              }));
            }
          });
      } else {
        setMemoData(undefined);
        setErrorMessages([]);
      }
    }
  };

  const handleSetCaseValue = (item: memoCaseDetails) => {
    setIsPopupOpen(false);
    const newCaseDetails = {
      id: Number(item?.id),
      name: item?.name,
      referenceId: item?.referenceId,
      typeName: item?.typeName,
    };

    setMemoCaseDetails(newCaseDetails);

    // If there's an uploaded file, re-process it with the new case ID
    const uploadedMemoFile = watch('uploadedMemo');
    if (uploadedMemoFile && autoFillMemo) {
      setIsMemoUploading(true);
      setErrorMessages([]);
      setWarningMessages([]);

      uploadPdfSettlement(uploadedMemoFile, Number(item?.id), undefined, settlementType)
        .then((res) => {
          setIsMemoUploading(false);
          setMemoData(res.data);
          setErrorMessages(res.data.errorMessages || []);

          if (res.data.caseInfo?.defendant && Array.isArray(res.data.caseInfo.defendant) && res.data.caseInfo.defendant.length === 1) {
            setMemoDefendantDetails({
              id: res.data.caseInfo.defendant[0].id,
              name: res.data.caseInfo.defendant[0].name || '',
            });
          }
          setWarningMessages(res.data.warningMessages || []);
        })
        .catch((err) => {
          setIsMemoUploading(false);
          const errorMessage = err.response?.data?.message;
          if (errorMessage) {
            setErrorMessages([errorMessage]);
          } else {
            setAlert((prev) => ({
              ...prev,
              message: errorMessage || 'Failed to process memo',
              type: 'error',
            }));
          }
        });
    } else {
      // Clear memo-related data if no memo uploaded
      setMemoData(undefined);
      setMemoDefendantDetails(undefined);
      setErrorMessages([]);
      setWarningMessages([]);
    }
  };

  const onSubmitForm = (values: SettlementCreateFormSubmitProps) => {
    setIsFormSubmitting(true);

    // If auto-fill toggle is enabled, use uploadPdfSettlement API (amount taken from PDF)
    if (autoFillMemo && memoData && values.uploadedMemo) {
      uploadPdfPreviewConfirm(
        values.uploadedMemo,
        memoData,
        memoDefendantDetails?.id,
        values.settlementType,
      )
        .then((res) => {
          setIsFormSubmitting(false);
          navigate('/payments/settlement-draft', { state: res.data });

          setAlert((prev) => ({
            ...prev,
            message: 'Settlement draft created successfully.',
            type: 'success',
          }));

        })
        .catch((err) => {
          setIsFormSubmitting(false);

          // Check if it's an amount mismatch error
          const errorMessage = err.response?.data?.message;
          if (errorMessage && errorMessage.includes('PDF processing failed')) {
            setIsErrorPopupOpen(true);
          } else {
            setAlert((prev) => ({
              ...prev,
              message: errorMessage || 'Failed to create settlement draft',
              type: 'error',
            }));
          }
        });
    } else {
      // Use original createSettlement API
      if (
        !values.defendant ||
        !memoCaseDetails?.id ||
        (showSettledAmount && (values.amount === undefined || values.amount === null)) ||
        (showPipAmount && (values.pipAmount === undefined || values.pipAmount === null)) ||
        (showLienReimbursementAmount &&
          (values.lienReimbursementAmount === undefined || values.lienReimbursementAmount === null))
      ) {
        setIsFormSubmitting(false);
        return;
      }

      const submitData = {
        defendantId: Number(values.defendant.id),
        caseId: Number(memoCaseDetails.id),
        settlementType: values.settlementType ?? 'standard',
        settledAmount: values.amount ?? 0,
        ...(showPipAmount && { pipAmount: values.pipAmount ?? 0 }),
        ...(showLienReimbursementAmount && {
          lienReimbursementAmount: values.lienReimbursementAmount ?? 0,
        }),
      };

      createSettlement(submitData, values.uploadedMemo || undefined)
        .then((res) => {
          setIsFormSubmitting(false);
          navigate(`/payments/settlement/${res.data.id}/home`);

          setAlert((prev) => ({
            ...prev,
            message: 'Settlement created successfully.',
            type: 'success',
          }));
        })
        .catch((err) => {
          setIsFormSubmitting(false);
          setAlert((prev) => ({
            ...prev,
            message: err.response.data.message,
            type: 'error',
          }));
        });
    }
  };

  const handleCancelChanges = () => {
    reset({
      ...SETTLEMENTS_CREATE_FORM_DEFAULT_VALUES,
      autoFillMemo: enableMemoUpload,
      uploadedMemo: null,
    });
    if (!caseId) setMemoCaseDetails(undefined);
    else setDefendantAutoSelectKey((prev) => prev + 1);
    setErrorMessages([]);
    setWarningMessages([]);
  };

  const handleDeleteFile = () => {
    setErrorMessages([]);
    setWarningMessages([]);
  }

  useEffect(() => {
    if (!caseId) return;

    getCase(Number(caseId)).then((res) => {
      setMemoCaseDetails({
        id: res.data.id,
        name: res.data.name,
        referenceId: res.data.referenceId,
        typeName: res.data.type.name,
      });
    });
  }, [caseId]);

  // Update toggle when enableMemoUpload changes
  useEffect(() => {
    setValue('autoFillMemo', enableMemoUpload);
  }, [enableMemoUpload, reset]);

  // Auto-select the defendant when the selected case has exactly one
  useEffect(() => {
    if (!memoCaseDetails?.id) return;

    let isCancelled = false;

    handleGetDefendants({
      page: 1,
      size: 2,
      count: true,
      filters: { caseId: memoCaseDetails.id },
    })
      .then((res) => {
        const defendants = res.data?.resultSet || [];
        if (isCancelled || defendants.length !== 1) return;

        const [defendant] = mapDefendantsQueryResults(defendants);
        setValue('defendant', { id: String(defendant.id), label: defendant.label }, {
          shouldValidate: true,
        });
      })
      .catch(() => undefined);

    return () => {
      isCancelled = true;
    };
  }, [memoCaseDetails?.id, defendantAutoSelectKey]);

  useEffect(() => {
    if (memoDefendantDetails) {
      setValue('defendant', { id: String(memoDefendantDetails.id), label: memoDefendantDetails.name });
    }
  }, [memoDefendantDetails]);

  // Re-process uploaded file when autoFillMemo is turned ON
  useEffect(() => {
    if (!autoFillMemo) {
      setErrorMessages([]);
      setWarningMessages([]);
      setMemoData(undefined);
    } else {
      const uploadedMemoFile = watch('uploadedMemo');

      if (
        autoFillMemo &&
        uploadedMemoFile &&
        memoData?.settlementType !== settlementType?.toUpperCase()
      ) {
        setIsMemoUploading(true);
        setErrorMessages([]);
        setWarningMessages([]);
        const finalCaseId = Number(memoCaseDetails?.id);

        uploadPdfSettlement(uploadedMemoFile, finalCaseId, undefined, settlementType)
          .then((res) => {
            setIsMemoUploading(false);
            setMemoData(res.data);
            setErrorMessages(res.data.errorMessages || []);
            res.data.caseInfo && setMemoCaseDetails({
              id: res.data.caseInfo?.id,
              name: res.data.caseInfo?.name,
              referenceId: res.data.caseInfo?.referenceId,
              typeName: res.data.caseInfo?.type?.name,
            });
            if (res.data.caseInfo?.defendant && Array.isArray(res.data.caseInfo.defendant) && res.data.caseInfo.defendant.length === 1) {
              setMemoDefendantDetails({
                id: res.data.caseInfo.defendant[0].id,
                name: res.data.caseInfo.defendant[0].name || '',
              });
            }
            setWarningMessages(res.data.warningMessages || []);
          })
          .catch((err) => {
            setIsMemoUploading(false);
            const errorMessage = err.response?.data?.message;
            if (errorMessage) {
              setErrorMessages([errorMessage]);
            } else {
              setAlert((prev) => ({
                ...prev,
                message: errorMessage || 'Failed to process memo',
                type: 'error',
              }));
            }
          });
      }
    }
  }, [autoFillMemo, settlementType]);



  return (
    <>
      {isFormSubmitting && (
        <Box className={styles.classes.loaderOverlay}>
          <Loader colorType="info" />
        </Box>
      )}
      <Box className={styles.classes.formWrapper}>
        <FormProvider {...formMethods}>
          <form className={styles.classes.formContainer}>
            <Box className={styles.classes.formInfoBlock}>
              <Typography className={styles.classes.fieldHeadline}>Case</Typography>
              <Box className={styles.classes.caseInfoBlock}>
                {/* {caseDetails && <CaseInfoPreview caseItem={caseDetails} />} */}
                {memoCaseDetails && <CaseInfoPreview memoCaseItem={memoCaseDetails} />}
                {!caseId && <FindCaseButton onButtonClick={() => setIsPopupOpen(true)} />}
              </Box>

              <Typography className={styles.classes.fieldHeadline} mt="32px">
                Settlement Type
              </Typography>

              <Controller
                name="settlementType"
                control={formMethods.control}
                render={({ field }) => (
                  <Box mt="16px" className={styles.classes.settlementTypeOptionsContainer}>
                    {Object.values(SETTLEMENT_TYPE_OPTIONS).map((option) => (
                      <Box
                        key={option.value}
                        className={clsx(styles.classes.settlementTypeCard, {
                          [styles.classes.settlementTypeCardSelected]: field.value === option.value,
                        })}
                        onClick={() => field.onChange(option.value)}
                      >
                        <Typography className={styles.classes.settlementTypeLabel}>
                          {option.label}
                        </Typography>
                        <Typography className={styles.classes.settlementTypeDescription}>
                          {option.description}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}
              />

              <Typography className={styles.classes.fieldHeadline} mt="32px">
                Defendant
              </Typography>

              <Box mt="16px">
                <FormAutocompleteFieldServerSearch
                  key={(memoCaseDetails?.id) ? String(memoCaseDetails?.id) : 'no-case'}
                  name="defendant"
                  label={`Defendant${autoFillMemo ? '' : ' *'}`}
                  optionsQuery={handleGetDefendants}
                  mapQueryResults={mapDefendantsQueryResults}
                  disabled={!memoCaseDetails}
                  filters={{ caseId: memoCaseDetails?.id ?? undefined }}
                  isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
                  fetchSize={100}
                  minSearchChars={3}
                />
              </Box>

              <Box mt="32px">
                {enableMemoUpload ? (
                  <Box display="flex" alignItems="center" gap="8px">
                    <FormToggleField
                      name="autoFillMemo"
                      label="Use memo to auto-fill settlement details"
                    />
                    <Tooltip
                      title="Enable to auto-fill settlement details from the memo. Turn off to create manually."
                      arrow
                      componentsProps={{
                        tooltip: {
                          sx: (theme) => ({
                            backgroundColor: theme.palette.common.white,
                            color: theme.palette.primary.light,
                            borderRadius: '8px',
                            boxShadow: 3,
                            fontFamily: 'Inter',
                            fontSize: 13,
                            fontWeight: 400,
                            padding: '8px 16px',
                            width: '100%'
                          })
                        },
                        arrow: {
                          sx: (theme) => ({
                            color: theme.palette.common.white
                          })
                        }
                      }}
                    >
                      <InfoIcon sx={{ color: theme.palette.common.white, fontSize: '20px', height: 'auto', flexShrink: 0 }} />

                    </Tooltip>
                  </Box>
                ) : (
                  <Tooltip
                    title="The option to create settlements using a memo is not available for this firm"
                    arrow
                    componentsProps={{
                      tooltip: {
                        sx: (theme) => ({
                          backgroundColor: theme.palette.common.white,
                          color: theme.palette.primary.light,
                          borderRadius: '8px',
                          boxShadow: 3,
                          fontFamily: 'Inter',
                          fontSize: 13,
                          fontWeight: 400,
                          padding: '8px 16px',
                          maxWidth: 260
                        })
                      },
                      arrow: {
                        sx: (theme) => ({
                          color: theme.palette.common.white
                        })
                      }
                    }}
                  >

                    <span>
                      <FormToggleField
                        name="autoFillMemo"
                        label="Opt for Auto-Fill using Memo Data (optional)"
                        disabled
                      />
                    </span>
                  </Tooltip>
                )}
              </Box>
            </Box>

            {!autoFillMemo && (
              <Box className={styles.classes.formSecondInfoBlock}>
                {showSettledAmount && (
                  <>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                      <Typography className={styles.classes.fieldHeadline}>Settled Amount</Typography>
                    </Box>

                    <Box mt="16px">
                      <FormTextField name="amount" label="Amount *" />
                    </Box>
                  </>
                )}

                {showPipAmount && (
                  <>
                    <Box display="flex" alignItems="center" justifyContent="space-between" mt={showSettledAmount ? '32px' : 0}>
                      <Typography className={styles.classes.fieldHeadline}>PIP Amount</Typography>
                    </Box>

                    <Box mt="16px">
                      <FormTextField name="pipAmount" label="PIP Amount *" />
                    </Box>
                  </>
                )}
                {settlementType === 'standard_pip' &&
                  Number(watch('amount')) === 0 &&
                  Number(watch('pipAmount')) > 0 && (
                    <Box className={styles.classes.warningMessageContainer} mt={3}>
                      <Box display="flex" gap="8px">
                        <InfoIcon
                          sx={{
                            color: theme.palette.warning.main,
                            fontSize: '20px',
                            height: 'auto',
                            flexShrink: 0,
                          }}
                        />
                        <Typography className={styles.classes.warningMessageText}>
                          This will create a Settlement + PIP with a $0.00 settlement amount.
                        </Typography>
                      </Box>
                    </Box>
                  )}

                {showLienReimbursementAmount && (
                  <>
                    <Box display="flex" alignItems="center" justifyContent="space-between" mt="32px">
                      <Typography className={styles.classes.fieldHeadline}>Lien Reimbursement Amount</Typography>
                    </Box>

                    <Box mt="16px">
                      <FormTextField name="lienReimbursementAmount" label="Lien Reimbursement Amount *" />
                    </Box>
                  </>
                )}
                {showLienReimbursementAmount &&
                  Number(watch('amount')) === 0 &&
                  Number(watch('lienReimbursementAmount')) > 0 && (
                    <Box className={styles.classes.warningMessageContainer} mt={3}>
                      <Box display="flex" gap="8px">
                        <InfoIcon
                          sx={{
                            color: theme.palette.warning.main,
                            fontSize: '20px',
                            height: 'auto',
                            flexShrink: 0,
                          }}
                        />
                        <Typography className={styles.classes.warningMessageText}>
                          This will create a Settlement + Lien Reimbursement with a $0.00 settlement amount.
                        </Typography>
                      </Box>
                    </Box>
                  )}
              </Box>
            )}

            {(
              <Box mt="32px" width="100%">
                <Typography className={styles.classes.fieldHeadline}>
                  {autoFillMemo ? 'Upload Memo' : 'Upload Memo (Optional)'}
                </Typography>

                {isMemoUploading && (
                  <Box mt="16px">
                    <Loader colorType="info" />
                  </Box>
                )}

                <Box mt="16px">
                  <Controller
                    name="uploadedMemo"
                    control={formMethods.control}
                    render={({ field }) => (
                      <FormFileUploadField
                        uploadLabel="Upload Memo"
                        uploadText="Click to upload or drag and drop"
                        uploadSubText="PDF (max 100MB)"
                        acceptedTypes={['application/pdf']}
                        maxFileSize={100 * 1024 * 1024}
                        onFileChange={(file) => handleMemoFileChange(file, field.onChange)}
                        value={field.value}
                        inputId="memo-file-input"
                        onRemoveFile={handleDeleteFile}
                      />
                    )}
                  />
                </Box>

                {errorMessages.length > 0 && (
                  <Box className={styles.classes.errorMessageContainer} mt={3}>
                    <Box display="flex" gap="8px">
                      <WarningIcon sx={{ color: theme.palette.error.main, fontSize: '20px', height: 'auto' }} />
                      <Box display="flex" flexDirection="column" gap="3px" width="100%">
                        {errorMessages.map((msg, index) => (
                          <Typography key={index} className={styles.classes.errorMessageText}>
                            {msg}
                          </Typography>
                        ))}
                      </Box>
                    </Box>
                  </Box>
                )}

                {warningMessages.length > 0 && (
                  <Box className={styles.classes.warningMessageContainer} mt={3}>
                    <Box display="flex" gap="8px">
                      <InfoIcon sx={{ color: theme.palette.warning.main, fontSize: '20px', height: 'auto', flexShrink: 0 }} />
                      <Box display="flex" flexDirection="column" gap="3px" width="100%">
                        {warningMessages.map((msg, index) => (
                          <Typography key={index} className={styles.classes.warningMessageText}>
                            {msg}
                          </Typography>
                        ))}
                      </Box>
                    </Box>
                  </Box>
                )}
              </Box>
            )}

            <Box display="flex" width="100%" justifyContent="center">
              <Box
                display="flex"
                mt="32px"
                maxWidth="416px"
                flexDirection="column"
                gap="16px"
                width="100%"
              >
                <FormSubmitButton
                  disabled={!isValid || !memoCaseDetails || isFormSubmitting || errorMessages.length > 0 || isMemoUploading}
                  buttonIcon={<SaveIcon />}
                  buttonText="Save"
                  onClick={handleSubmit(onSubmitForm)}
                />
                <FormCancelButton
                  disabled={caseId ? !isDirty : !(isDirty || memoCaseDetails)}
                  onClick={handleCancelChanges}
                />
              </Box>
            </Box>
          </form>
        </FormProvider>
        <MemoSearchCasePopup
          isOpen={isPopupOpen}
          onMainButtonClick={handleSetCaseValue}
          onSecondaryButtonClick={() => setIsPopupOpen(false)}
        />
        <UnableMemoPopup
          isOpen={isErrorPopupOpen}
          onMainButtonClick={() => setIsErrorPopupOpen(false)}
          onSecondaryButtonClick={() => setIsErrorPopupOpen(false)}
        />
      </Box >
    </>
  );
};

export default CreateSettlementFormContainer;